import React, {
  useCallback,
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  GoogleMap,
  InfoWindow,
  LoadScript,
  Marker,
  StandaloneSearchBox,
  useLoadScript,
} from "@react-google-maps/api";
import styled from "styled-components";

declare global {
  interface Window {
    google: any;
  }
}

interface AddressComponents {
  street?: string;
  city?: string;
  state?: string;
  postCode?: string;
  country?: string;
  formattedAddress: string;
}

export interface MapPickerRef {
  setLocation: (lat: number, lng: number) => void;
}

interface MapPickerProps {
  onLocationSelect: (
    lat: number,
    lng: number,
    address: string,
    addressComponents?: AddressComponents
  ) => void;
  initialLocation?: {
    lat: number;
    lng: number;
  };
  addressQuery?: string;
}

const containerStyle = {
  width: "100%",
  height: "300px",
};

const defaultCenter = {
  lat: 51.510244,
  lng: 0.1257199,
};

const MapContainer = styled.div`
  margin: 1rem 0;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
`;

const SearchInput = styled.input`
  box-sizing: border-box;
  border: 1px solid #e2e8f0;
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border-radius: 4px;
  font-size: 14px;
  outline: none;
  margin-bottom: 1rem;
  transition: border-color 0.2s;

  &:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const ErrorText = styled.div`
  color: #ef4444;
  font-size: 0.875rem;
  margin-top: 0.5rem;
  padding: 0.5rem;
  background: #fef2f2;
  border-radius: 4px;
  border: 1px solid #fecaca;
`;

const MapPicker = forwardRef<MapPickerRef, MapPickerProps>(
  ({ onLocationSelect, initialLocation, addressQuery }, ref) => {
    const [map, setMap] = useState<google.maps.Map | null>(null);

    const [markerPosition, setMarkerPosition] =
      useState<google.maps.LatLngLiteral>(initialLocation || defaultCenter);
    const [searchBox, setSearchBox] =
      useState<google.maps.places.SearchBox | null>(null);
    const [error, setError] = useState<string | null>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const [searchValue, setSearchValue] = useState("");
    const [isInitialized, setIsInitialized] = useState(false);

    // Handle external location changes
    const setLocation = useCallback(
      (lat: number, lng: number) => {
        if (!map) return;

        const position = { lat, lng };
        setMarkerPosition(position);
        map.panTo(position);

        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode(
          { location: position },
          (
            results: google.maps.GeocoderResult[] | null,
            status: google.maps.GeocoderStatus
          ) => {
            if (status === "OK" && results?.[0]) {
              const addressComponents = extractAddressComponents(results[0]);
              setSearchValue(results[0].formatted_address || "");
              onLocationSelect(
                position.lat,
                position.lng,
                results[0].formatted_address || "",
                addressComponents
              );
            }
          }
        );
      },
      [map, onLocationSelect]
    );

    // Expose setLocation via ref
    useImperativeHandle(ref, () => ({
      setLocation,
    }));

    // Initialize map with initial location if provided
    useEffect(() => {
      if (initialLocation && map && !isInitialized) {
        setLocation(initialLocation.lat, initialLocation.lng);
        setIsInitialized(true);
      }
    }, [initialLocation, map, isInitialized, setLocation]);

    // Handle external address query changes
    useEffect(() => {
      if (!map || !addressQuery || addressQuery === searchValue) return;

      const placesService = new window.google.maps.places.PlacesService(map);
      const request = {
        query: addressQuery,
        fields: ["formatted_address", "geometry", "name"],
      };

      placesService.findPlaceFromQuery(
        request,
        (
          results: google.maps.places.PlaceResult[] | null,
          status: google.maps.places.PlacesServiceStatus
        ) => {
          if (status === "OK" && results?.[0]?.geometry?.location) {
            const location = results[0].geometry.location;
            setSearchValue(addressQuery);
            setLocation(location.lat(), location.lng());
          }
        }
      );
    }, [addressQuery, map, searchValue, setLocation]);

    const onMapLoad = useCallback((map: google.maps.Map) => {
      setMap(map);
    }, []);

    const onSearchBoxLoad = useCallback((ref: google.maps.places.SearchBox) => {
      setSearchBox(ref);
    }, []);

    const extractAddressComponents = (
      place: google.maps.GeocoderResult | google.maps.places.PlaceResult
    ): AddressComponents => {
      const addressComponents: AddressComponents = {
        street: "",
        city: "",
        state: "",
        postCode: "",
        country: "",
        formattedAddress: place.formatted_address || "",
      };

      const components =
        "address_components" in place ? place.address_components : [];

      if (components) {
        components.forEach((component) => {
          const types = component.types;
          const value = component.long_name;

          if (types.includes("street_number") || types.includes("route")) {
            addressComponents.street = addressComponents.street
              ? `${addressComponents.street} ${value}`
              : value;
          } else if (types.includes("locality")) {
            addressComponents.city = value;
          } else if (types.includes("administrative_area_level_1")) {
            addressComponents.state = value;
          } else if (types.includes("postal_code")) {
            addressComponents.postCode = value;
          } else if (types.includes("country")) {
            addressComponents.country = value;
          }
        });
      }

      return addressComponents;
    };

    const onPlacesChanged = useCallback(() => {
      if (!searchBox) return;

      try {
        const places = searchBox.getPlaces();
        if (!places || places.length === 0) return;

        const place = places[0];
        const location = place.geometry?.location;
        if (!location) return;

        const position = {
          lat: location.lat(),
          lng: location.lng(),
        };

        setMarkerPosition(position);
        map?.panTo(position);

        const addressComponents = extractAddressComponents(place);
        const formattedAddress = place.formatted_address || "";
        setSearchValue(formattedAddress);
        setError(null);

        onLocationSelect(
          position.lat,
          position.lng,
          formattedAddress,
          addressComponents
        );
      } catch (err) {
        console.error("Error processing place selection:", err);
        setError("Failed to process the selected location. Please try again.");
      }
    }, [searchBox, map, onLocationSelect]);

    const onMapClick = useCallback(
      async (e: google.maps.MapMouseEvent) => {
        if (!e.latLng) return;

        const position = {
          lat: e.latLng.lat(),
          lng: e.latLng.lng(),
        };

        setMarkerPosition(position);

        const geocoder = new window.google.maps.Geocoder();
        try {
          const response = await new Promise<google.maps.GeocoderResult[]>(
            (resolve, reject) => {
              geocoder.geocode(
                { location: position },
                (
                  results: google.maps.GeocoderResult[] | null,
                  status: google.maps.GeocoderStatus
                ) => {
                  if (status === "OK" && results) {
                    resolve(results);
                  } else {
                    reject(new Error("Geocoder failed"));
                  }
                }
              );
            }
          );

          if (response?.[0]) {
            const place = response[0];
            const addressComponents = extractAddressComponents(place);
            setError(null);

            onLocationSelect(
              position.lat,
              position.lng,
              place.formatted_address || "",
              addressComponents
            );
          }
        } catch (err) {
          console.error("Error during geocoding:", err);
          setError(
            "Failed to get address for the selected location. Please try again."
          );
        }
      },
      [onLocationSelect]
    );

    if (!process.env.REACT_APP_GOOGLE_MAPS_API_KEY) {
      return (
        <MapContainer>
          <ErrorText>
            Google Maps API key is not configured. Please add
            REACT_APP_GOOGLE_MAPS_API_KEY to your environment variables.
          </ErrorText>
        </MapContainer>
      );
    }
    return (
      <LoadScript
        googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
        libraries={["places"]}
        onLoad={() => console.log("Google Maps API loaded successfully")}
        onError={(error) => {
          console.error("Error loading Google Maps API:", error);
          setError(
            "Failed to load Google Maps. Please check your internet connection."
          );
        }}
      >
        <MapContainer>
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={markerPosition}
            zoom={14}
            onLoad={onMapLoad}
            onClick={onMapClick}
            options={{
              zoomControl: true,
              streetViewControl: false,
              mapTypeControl: false,
              fullscreenControl: true,
            }}
          >
            <StandaloneSearchBox
              onLoad={onSearchBoxLoad}
              onPlacesChanged={onPlacesChanged}
            >
              <SearchInput
                ref={searchInputRef}
                placeholder="Search for a location or click on the map"
                type="text"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
              />
            </StandaloneSearchBox>
            {/* {window.google && window.google.maps && (
            <Marker
              position={markerPosition}
              icon={{
                url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
                scaledSize: new window.google.maps.Size(40, 40),
                origin: new window.google.maps.Point(0, 0),
                anchor: new window.google.maps.Point(20, 40)
              }}
            />
          )} */}
            {/* <Marker
              position={markerPosition}
              // icon={{
              //   url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
              //   scaledSize: new window.google.maps.Size(40, 40),
              //   origin: new window.google.maps.Point(0, 0),
              //   anchor: new window.google.maps.Point(20, 40)
              // }}
            /> */}
            {/* Start with default marker to rule out icon issues */}
            {markerPosition && <Marker position={markerPosition} />}

            {/* Uncomment to test InfoWindow presence (quick test) */}
            {markerPosition && (
              <InfoWindow position={markerPosition}>
                <div>123</div>
              </InfoWindow>
            )}
          </GoogleMap>
          {error && <ErrorText>{error}</ErrorText>}
        </MapContainer>
      </LoadScript>
    );
  }
);


export default MapPicker;
